import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AIAPPSPACE_BOT_SLUG,
  submitLearning,
  validateLearningDocument,
} from '../api/aiLearningApi';

const starterPrompts = [
  {
    title: 'App catalog',
    text: 'Add current app names, categories, platform links, and what each app is best used for.',
  },
  {
    title: 'Support policy',
    text: 'Add refund, account deletion, subscription, privacy, and escalation rules for AiAppSpace users.',
  },
  {
    title: 'Release notes',
    text: 'Add recent feature changes, known issues, deprecated flows, and recommended user guidance.',
  },
];

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-panel-border bg-black/25 px-3 py-2">
      <p className="text-[9px] uppercase tracking-wider text-muted">{label}</p>
      <p className="text-lg font-semibold text-white">{value}</p>
    </div>
  );
}

export default function AiAppSpaceLearning() {
  const fileInputRef = useRef(null);
  const [content, setContent] = useState('');
  const [document, setDocument] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const clearDocument = () => {
    setDocument(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePickDocument = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const err = validateLearningDocument(file);
    if (err) {
      toast.error(err);
      clearDocument();
      return;
    }

    setDocument(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() && !document) {
      toast.error('Please enter learning content or upload a document.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await submitLearning({
        appSlug: AIAPPSPACE_BOT_SLUG,
        content: content.trim(),
        document,
      });
      toast.success(res.message || 'AiAppSpace learning submitted.');
      setContent('');
      clearDocument();
    } catch (err) {
      toast.error(err.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const applyStarter = (text) => {
    setContent((current) => (current.trim() ? `${current.trim()}\n\n${text}` : text));
  };

  return (
    <section className="space-y-4 w-full">
      <header className="rounded-2xl border border-indigo-700/35 bg-gradient-to-br from-indigo-950/55 via-[#121427] to-black/50 px-5 py-5 sm:px-7 sm:py-6 shadow-[0_20px_80px_rgba(0,0,0,0.45)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-indigo-200/90 font-semibold mb-1.5">
              AI Learning
            </p>
            <h1 className="text-2xl sm:text-3xl font-serif text-white tracking-tight">
              AiAppSpace Bot
            </h1>
            <p className="text-xs text-muted mt-2 max-w-2xl leading-relaxed">
              Dedicated training workspace for AiAppSpace support, app catalog details, policies,
              and product updates.
            </p>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border border-indigo-500/40 bg-indigo-950/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-100/95">
            <span
              className="h-2 w-2 rounded-full bg-indigo-300 shadow-[0_0_10px_rgba(165,180,252,0.7)]"
              aria-hidden
            />
            Knowledge queue
          </span>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Stat label="Target app" value="AiAppSpace" />
        <Stat label="Accepted files" value="PDF TXT DOC" />
        <Stat label="Max upload" value="10 MB" />
      </div>

      <div className="card">
        <div>
          <h2 className="card-title">Submit AiAppSpace Knowledge</h2>
          <p className="card-subtitle">
            Add text or upload a document for the AiAppSpace bot learning pipeline.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="label" htmlFor="aiappspace-content">
              Learning content
            </label>
            <textarea
              id="aiappspace-content"
              className="input min-h-[160px] resize-y w-full"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter facts, policies, app details, or support context for AiAppSpace..."
              disabled={submitting}
            />
          </div>

          <div>
            <p className="label">Quick starters</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {starterPrompts.map((starter) => (
                <button
                  key={starter.title}
                  type="button"
                  className="rounded-lg border border-panel-border bg-black/20 px-3 py-2 text-left hover:border-indigo-400/70 transition-colors"
                  onClick={() => applyStarter(starter.text)}
                  disabled={submitting}
                >
                  <span className="block text-xs font-semibold text-white">{starter.title}</span>
                  <span className="block text-[10px] text-muted mt-1 leading-relaxed">
                    {starter.text}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 py-1">
            <span className="flex-1 h-px bg-panel-border" />
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted">or</span>
            <span className="flex-1 h-px bg-panel-border" />
          </div>

          <div>
            <label className="label">Upload document</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,.doc,.docx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={handlePickDocument}
              disabled={submitting}
            />
            <button
              type="button"
              className="btn-ghost w-full text-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
            >
              Choose document
            </button>
            <p className="text-[10px] text-muted mt-1">PDF, TXT, DOC, or DOCX - max 10MB</p>
            {document && (
              <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-panel-border px-3 py-2">
                <div className="min-w-0">
                  <p className="text-xs text-white font-semibold truncate">{document.name}</p>
                  <p className="text-[10px] text-muted">{formatFileSize(document.size)}</p>
                </div>
                <button
                  type="button"
                  className="btn-ghost text-[10px] py-1 px-2 shrink-0"
                  onClick={clearDocument}
                  disabled={submitting}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full mt-1">
            {submitting ? 'Submitting...' : 'Submit AiAppSpace learning'}
          </button>
        </form>
      </div>
    </section>
  );
}
